import { TrustCheckCard } from "@/components/features/matter/trust-checks/TrustCheckCard";
import {
  ActionConfirmationModal,
  AppModal,
  Button,
  DateFormField,
  Form,
  FormSubmitButton,
  MultiselectFormField,
  SimpleSelectFormField,
  SwitchFormField,
  TextAreaFormField,
  TextFormField,
  Typography,
} from "@/components/ui";
import { CurrencyWrapper } from "@/components/ui/CurrencyWrapper/CurrencyWrapper";
import { AddContactFormField } from "@/components/ui/FormFields/AddContactFormField/AddContactFormField";
import { CurrencyFormField } from "@/components/ui/FormFields/CurrencyFormField/CurrencyFormField";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { MatterContactConnectionModalPreview } from "@/components/ui/MatterContactConnectionRichCard/MatterContactConnectionModalPreview";
import { useUser } from "@/components/UserProvider";
import { useExpensesViewModel } from "@/hooks/matter-view-models/use-expenses-view-model";
import { useSyncExpensePayeeConnection } from "@/lib/data/react-query/connections/expense-contact-connection";
import { retrieveMatterQueryOptions } from "@/lib/data/react-query/matters/matter";
import {
  useCreateMatterExpense,
  useDeleteMatterExpense,
  useUpdateMatterExpense,
} from "@/lib/data/react-query/matters/matter-expenses";
import { ContactConnectionInFormSchema } from "@/lib/data/schemas";
import { MatterExpenseSchema } from "@/lib/data/schemas/matters/MatterSchema";
import { MATTER_CONTACT_CONNECTION_DEFAULT_VALUES } from "@/lib/data/static/formStatics";
import {
  MATTER_EXPENSE_CATEGORY_OPTIONS,
  MATTER_EXPENSE_PAID_BY_METHOD_OPTIONS,
  MATTER_EXPENSE_PAID_STATUS_OPTIONS,
} from "@/lib/data/static/matters/expenseStatics";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import {
  SLIDEOVER_DESCRIPTION,
  SLIDEOVER_TITLE,
} from "@/lib/data/static/slideoverStatics";
import type { Contact, MatterExpense } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { mergeInitialData } from "@/lib/utils/initialDataUtils";
import { calculateExpenseTotals } from "@/lib/utils/matters/expense-utils";
import {
  createExpensePayeeConnectionChildInput,
  getExpensePayeeConnection,
} from "@/lib/utils/connections/expense-contact-connections";
import { getConnectionFormContact } from "@/lib/utils/connections/item-contact-connections";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { useModals } from "@/providers/ModalProvider";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSuspenseQuery } from "@tanstack/react-query";
import { isNil, omit, omitBy } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { z } from "zod";

interface AddOrEditExpenseModalProps {
  title: string;
  description?: string;
  editing?: boolean;
  matterId: string;
  expenseId?: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  initialData?: Partial<MatterExpense>;
  splitMode?: boolean;
  /** Carried from global modal payload (sub-item tab); reserved if tabbed expense UI returns. */
  initialTab?: "details" | "notes" | "tasks" | "references" | "events";
}

const expenseFormSchema = MatterExpenseSchema.omit({
  connections: true,
  connectionsNextCursor: true,
  dateCreated: true,
  dateModified: true,
  matterId: true,
  id: true,
})
  .extend({
    refunded: z.boolean(),
    matterContactConnectionPayee: ContactConnectionInFormSchema,
    category: z
      .string()
      .refine(
        (value) =>
          MATTER_EXPENSE_CATEGORY_OPTIONS.some((option) => option === value),
        { message: "Select a category" },
      ),
    splitMatterIds: z.array(z.string()).optional(),
  })
  .superRefine((data, ctx) => {
    if (data.category === "Other") {
      if (!data.description?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Describe the category when selecting Other",
          path: ["description"],
        });
      }
    }
    if (data.paidAmount && data.paidAmount > (data.cost ?? 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Paid amount cannot be greater than the cost",
        path: ["paidAmount"],
      });
    }
  });

const defaultFormValues = {
  cost: null,
  label: "",
  category: "",
  description: "",
  dateIncurred: "",
  paidAmount: 0,
  paidStatus: "Unpaid" as const,
  paidByMethod: "",
  paidByMethodDetails: "",
  refunded: false,
  refundedAmount: null,
  reducedAmount: null,
  matterContactConnectionPayee: MATTER_CONTACT_CONNECTION_DEFAULT_VALUES,
  splitMatterIds: [],
};

export const AddOrEditExpenseModal = ({
  title,
  description,
  open,
  setOpen,
  editing,
  matterId,
  expenseId,
  initialData,
  splitMode,
}: AddOrEditExpenseModalProps) => {
  const { hasActionPermission } = useUser();
  const { updateSlideoverPayload } = useModals();

  const { data: matterData } = useSuspenseQuery(
    retrieveMatterQueryOptions(matterId),
  );

  const matter = matterData.data;
  const expensesViewModel = useExpensesViewModel(matterId);

  const expense = expenseId
    ? expensesViewModel.find((exp) => exp.id === expenseId)
    : undefined;
  const expenseInitialValues = mergeInitialData(expense, initialData);
  const initialCategory = expenseInitialValues?.category;
  const hasCustomCategory =
    !!initialCategory &&
    !MATTER_EXPENSE_CATEGORY_OPTIONS.some(
      (option) => option === initialCategory,
    );

  const payee = getExpensePayeeConnection(expense);

  const expenseForm = useForm<z.infer<typeof expenseFormSchema>>({
    resolver: zodResolver(expenseFormSchema),
    values:
      editing && expense
        ? {
            ...defaultFormValues,
            ...(expenseInitialValues ?? {}),
            refunded: expenseInitialValues?.refunded ?? false,
            category: hasCustomCategory ? "Other" : (initialCategory ?? ""),
            // Preserve legacy custom categories in the API's description field.
            description: hasCustomCategory
              ? [initialCategory, expenseInitialValues.description]
                  .filter(Boolean)
                  .join("\n\n")
              : (expenseInitialValues?.description ?? ""),
            matterContactConnectionPayee: getConnectionFormContact(payee),
          }
        : {
            ...defaultFormValues,
          },
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const expenseCostFormValue = expenseForm.watch("cost");
  const expenseReducedAmountFormValue = expenseForm.watch("reducedAmount");
  const expensePaidStatusFormValue = expenseForm.watch("paidStatus");
  const expensePaidAmountFormValue = expenseForm.watch("paidAmount");
  const expenseRefundedFormValue = expenseForm.watch("refunded");
  const expenseRefundedAmountFormValue = expenseForm.watch("refundedAmount");
  const splitMatterIdsFormValue = expenseForm.watch("splitMatterIds") ?? [];
  const outstandingBalance = useMemo(() => {
    const { totalBalanceRemaining } = calculateExpenseTotals([
      {
        cost: expenseCostFormValue,
        reducedAmount: expenseReducedAmountFormValue,
        paidAmount: expensePaidAmountFormValue,
        refunded: expenseRefundedFormValue,
        refundedAmount: expenseRefundedAmountFormValue,
      },
    ]);
    return totalBalanceRemaining;
  }, [
    expenseCostFormValue,
    expenseReducedAmountFormValue,
    expensePaidAmountFormValue,
    expenseRefundedAmountFormValue,
    expenseRefundedFormValue,
  ]);
  const splitCount = splitMode ? splitMatterIdsFormValue.length + 1 : 1;
  const splitAmount = useMemo(() => {
    if (!splitMode) {
      return expenseCostFormValue ?? 0;
    }
    if (!expenseCostFormValue) {
      return 0;
    }
    return Number((expenseCostFormValue / splitCount).toFixed(2));
  }, [expenseCostFormValue, splitCount, splitMode]);

  const createExpense = useCreateMatterExpense();
  const updateExpense = useUpdateMatterExpense();
  const deleteExpense = useDeleteMatterExpense();
  const syncExpensePayee = useSyncExpensePayeeConnection();

  const isFormLoading =
    createExpense.isPending ||
    updateExpense.isPending ||
    deleteExpense.isPending ||
    syncExpensePayee.isPending;

  const [, setSelectedPayee] = useState<Contact | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const relatedMatterOptions = useMemo(
    () =>
      matter.relatedMatterConnections.map((relatedMatter) => ({
        label: relatedMatter.relatedMatterTitle ?? relatedMatter.title ?? "",
        value: relatedMatter.relatedMatterId,
      })),
    [matter.relatedMatterConnections],
  );

  const [closeAfterSave, setCloseAfterSave] = useState(false);
  const expenseModalFormId = "matter-expense-modal-form";

  const splitExpenseValue = (
    value: number | null | undefined,
    count: number,
  ) => {
    if (value === null || value === undefined) {
      return value;
    }
    return Number((value / count).toFixed(2));
  };

  const onSubmit = async (data: z.infer<typeof expenseFormSchema>) => {
    let resData;
    const { splitMatterIds, ...rest } = data;
    const expensePayload = omit(rest, ["matterContactConnectionPayee"]);
    const desiredPayeeId = data.matterContactConnectionPayee?.id;
    if (editing && expense) {
      const result = await updateExpense.mutateAsync({
        ...expensePayload,
        matterId: matter.id,
        dateModified: expense.dateModified,
        id: expense.id,
      });
      resData = result;
      await syncExpensePayee.mutateAsync({
        currentExpense: expense,
        desiredContactId: desiredPayeeId,
        expenseId: result.data.id,
        matterId: matter.id,
      });
    } else {
      if (splitMode) {
        if (!splitMatterIds?.length) {
          expenseForm.setError("splitMatterIds", {
            type: "manual",
            message: "Select at least one related matter to split with.",
          });
          return;
        }
        const targetMatterIds = [matter.id, ...new Set(splitMatterIds)];
        const splitPayload = {
          ...expensePayload,
          cost: splitExpenseValue(expensePayload.cost, targetMatterIds.length),
          reducedAmount: splitExpenseValue(
            expensePayload.reducedAmount,
            targetMatterIds.length,
          ),
          paidAmount: splitExpenseValue(
            expensePayload.paidAmount,
            targetMatterIds.length,
          ),
          refundedAmount: splitExpenseValue(
            expensePayload.refundedAmount,
            targetMatterIds.length,
          ),
        };

        const calculateRemainder = (
          value: number | null | undefined,
          splitValue: number | null | undefined,
        ) => {
          if (
            value === null ||
            value === undefined ||
            splitValue === null ||
            splitValue === undefined
          ) {
            return 0;
          }
          const totalDistributed = splitValue * targetMatterIds.length;
          return Number((value - totalDistributed).toFixed(2));
        };

        const costRemainder = calculateRemainder(
          expensePayload.cost,
          splitPayload.cost,
        );
        const reducedAmountRemainder = calculateRemainder(
          expensePayload.reducedAmount,
          splitPayload.reducedAmount,
        );
        const paidAmountRemainder = calculateRemainder(
          expensePayload.paidAmount,
          splitPayload.paidAmount,
        );
        const refundedAmountRemainder = calculateRemainder(
          expensePayload.refundedAmount,
          splitPayload.refundedAmount,
        );

        for (let i = 0; i < targetMatterIds.length; i++) {
          const targetMatterId = targetMatterIds[i];
          const isFirstMatter = i === 0;

          const matterPayload = isFirstMatter
            ? {
                ...splitPayload,
                cost: splitPayload.cost
                  ? splitPayload.cost + costRemainder
                  : splitPayload.cost,
                reducedAmount: splitPayload.reducedAmount
                  ? splitPayload.reducedAmount + reducedAmountRemainder
                  : splitPayload.reducedAmount,
                paidAmount: splitPayload.paidAmount
                  ? splitPayload.paidAmount + paidAmountRemainder
                  : splitPayload.paidAmount,
                refundedAmount: splitPayload.refundedAmount
                  ? splitPayload.refundedAmount + refundedAmountRemainder
                  : splitPayload.refundedAmount,
              }
            : splitPayload;

          const result = await createExpense.mutateAsync({
            matterId: targetMatterId,
            ...matterPayload,
            connections: desiredPayeeId
              ? [createExpensePayeeConnectionChildInput(desiredPayeeId)]
              : [],
          });
          resData = result;
        }
      } else {
        const result = await createExpense.mutateAsync({
          matterId: matter.id,
          ...expensePayload,
          connections: desiredPayeeId
            ? [createExpensePayeeConnectionChildInput(desiredPayeeId)]
            : [],
        });
        resData = result;
        updateSlideoverPayload("expense", {
          title: SLIDEOVER_TITLE.EXPENSE.UPDATE,
          description: SLIDEOVER_DESCRIPTION.EXPENSE.UPDATE,
          editing: true,
          expenseId: resData.data.id,
        });
      }
    }
    if (closeAfterSave) {
      setOpen(false);
    } else {
      const sanitizedResData = omitBy(resData?.data, isNil);
      expenseForm.reset({
        ...defaultFormValues,
        ...sanitizedResData,
        matterContactConnectionPayee: getConnectionFormContact(payee),
      });
      setSelectedPayee(null);
    }
  };

  const handleDelete = () => {
    if (!expense) return;

    deleteExpense.mutate({
      matterId: matter.id,
      id: expense.id,
    });
    setOpen(false);
  };

  useEffect(() => {
    if (!expenseRefundedFormValue) {
      return;
    }

    if (
      isNil(expenseRefundedAmountFormValue) &&
      !isNil(expensePaidAmountFormValue)
    ) {
      expenseForm.setValue("refundedAmount", expensePaidAmountFormValue);
    }
  }, [
    expenseForm,
    expensePaidAmountFormValue,
    expenseRefundedAmountFormValue,
    expenseRefundedFormValue,
  ]);

  useEffect(() => {
    if (!open) {
      expenseForm.reset();
      setSelectedPayee(null);
    }
  }, [open, expenseForm]);

  const expenseModalContactPickerProps = {
    contactPickerPresentation: "modal" as const,
    contactSelectorSize: "compact" as const,
    hideCompactContactChrome: true,
  };

  const renderPayeeContactPreview = useCallback(
    (contact: Contact) => (
      <MatterContactConnectionModalPreview
        contactId={contact.id}
        previewContact={contact}
        matterId={matter.id}
        matterContactConnection={
          payee && payee.targetObjectId === contact.id
            ? payee
            : ({
                label: "Payee",
                contactId: contact.id,
              } as never)
        }
        onRemoveContact={() => {
          expenseForm.setValue(
            "matterContactConnectionPayee",
            MATTER_CONTACT_CONNECTION_DEFAULT_VALUES,
            { shouldDirty: true },
          );
        }}
      />
    ),
    [payee, matter.id, expenseForm],
  );

  const expenseModalContactSidebar = (
    <div className="flex flex-col gap-2">
      <AddContactFormField
        {...expenseModalContactPickerProps}
        label="Payee"
        register={expenseForm.register("matterContactConnectionPayee")}
        setSelectedContact={setSelectedPayee}
        onDelete={() => {
          expenseForm.setValue(
            "matterContactConnectionPayee",
            MATTER_CONTACT_CONNECTION_DEFAULT_VALUES,
            { shouldDirty: true },
          );
        }}
        matterId={matter.id}
        defaultSearchField="name"
        renderSelectedContactPreview={renderPayeeContactPreview}
      />
    </div>
  );

  return (
    <>
      <Form {...expenseForm}>
        <AppModal
          open={open}
          onOpenChange={setOpen}
          title={title}
          description={description}
          size="4xl"
          loading={isFormLoading}
          isDirty={expenseForm.formState.isDirty}
          preview={expenseModalContactSidebar}
          previewLabel={null}
          bodyFormId={expenseModalFormId}
          bodyFormOnSubmit={expenseForm.handleSubmit(onSubmit, (errors) => {
            const message = Object.values(errors).find(
              (error) => typeof error.message === "string",
            )?.message;
            toast.error(
              typeof message === "string"
                ? message
                : "Check the highlighted cost fields before saving.",
            );
          })}
          footer={
            <div className="flex w-full flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                {editing && expense && (
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={() => {
                      setIsDeleting(true);
                    }}
                  >
                    Delete
                  </Button>
                )}
              </div>
              <div className="flex flex-wrap justify-end gap-2">
                <FormCancelButton
                  setOpen={setOpen}
                  isDirty={expenseForm.formState.isDirty}
                />
                <FormSubmitButton
                  submitFormId={expenseModalFormId}
                  title={editing ? "Save" : "Add cost"}
                  loading={isFormLoading}
                  overrideDisabled={Boolean(initialData)}
                  submitFn={() => {
                    setCloseAfterSave(true);
                  }}
                  disabled={isFormLoading}
                />
              </div>
            </div>
          }
        >
          <div className="overflow-y-auto">
            <fieldset
              className={cn(
                slideoverFieldsetClassName,
                "my-0 p-4 sm:p-5",
                "grid-cols-1 md:grid-cols-2",
              )}
              disabled={
                editing
                  ? !hasActionPermission(
                      ACTION_PERMISSION_KEYS.matterExpense,
                      ACTION_PERMISSION_VALUES.update,
                    )
                  : false
              }
            >
              <TextFormField
                register={expenseForm.register("label")}
                label="Label*"
              />
              <SimpleSelectFormField
                register={expenseForm.register("category")}
                label="Category*"
                items={MATTER_EXPENSE_CATEGORY_OPTIONS}
                placeholder="Select category"
              />
              <div className="col-span-2">
                <TextAreaFormField
                  register={expenseForm.register("description")}
                  label={
                    expenseForm.watch("category") === "Other"
                      ? "Description* (describe the other category)"
                      : "Description"
                  }
                />
              </div>

              <DateFormField
                register={expenseForm.register("dateIncurred")}
                name="dateIncurred"
                label="Date incurred"
              />
              <SimpleSelectFormField
                register={expenseForm.register("paidByMethod")}
                label="Paid by"
                items={MATTER_EXPENSE_PAID_BY_METHOD_OPTIONS}
                placeholder="Select method"
                clearable
              />
              <SimpleSelectFormField
                register={expenseForm.register("paidStatus")}
                label="Paid status"
                items={MATTER_EXPENSE_PAID_STATUS_OPTIONS}
                placeholder="Select status"
                clearable
                onChange={(value) => {
                  if (!value) return;
                  const totalCost =
                    expenseReducedAmountFormValue || expenseCostFormValue;
                  if (value === "Paid") {
                    expenseForm.setValue("paidAmount", totalCost);
                  } else if (value === "Unpaid") {
                    expenseForm.setValue("paidAmount", 0);
                  }
                }}
              />

              <TextFormField
                register={expenseForm.register("paidByMethodDetails")}
                label="Paid by details"
              />
              <CurrencyFormField
                register={expenseForm.register("cost")}
                label={splitMode ? "Total cost*" : "Original amount*"}
                onChange={(value) => {
                  if (
                    expensePaidStatusFormValue === "Paid" &&
                    !expenseReducedAmountFormValue
                  ) {
                    expenseForm.setValue("paidAmount", Number(value));
                  }
                }}
              />
              {!splitMode && (
                <>
                  <CurrencyFormField
                    register={expenseForm.register("reducedAmount")}
                    label="Reduced amount"
                    onChange={(value) => {
                      if (expensePaidStatusFormValue === "Paid") {
                        if (Number(value) && Number(value) !== 0) {
                          expenseForm.setValue("paidAmount", Number(value));
                        } else {
                          expenseForm.setValue(
                            "paidAmount",
                            expenseCostFormValue,
                          );
                        }
                      }
                    }}
                  />
                  <CurrencyFormField
                    register={expenseForm.register("paidAmount")}
                    label="Paid amount"
                    disabled={
                      expensePaidStatusFormValue === "Paid" ||
                      expensePaidStatusFormValue === "Unpaid"
                    }
                  />
                </>
              )}
              <div></div>
              <div className="flex items-center justify-start">
                <SwitchFormField
                  register={expenseForm.register("refunded")}
                  label="Refunded"
                  onChange={(checked) => {
                    if (!checked) {
                      expenseForm.setValue("refundedAmount", null, {
                        shouldDirty: true,
                      });
                    }
                  }}
                />
              </div>
              <div></div>
              {expenseForm.watch("refunded") && (
                <>
                  <CurrencyFormField
                    register={expenseForm.register("refundedAmount")}
                    label="Refunded amount"
                  />
                  <div></div>
                </>
              )}
              {splitMode && !editing && (
                <div className="col-span-2 space-y-2">
                  <MultiselectFormField
                    label="Split with related matters*"
                    register={expenseForm.register("splitMatterIds")}
                    options={relatedMatterOptions}
                    placeholder="Select related matters"
                    maxCount={Math.max(relatedMatterOptions.length, 1)}
                    disabled={relatedMatterOptions.length === 0}
                  />
                  {relatedMatterOptions.length === 0 && (
                    <Typography className="text-muted-foreground text-xs">
                      No related matters found for this case.
                    </Typography>
                  )}
                  {splitMatterIdsFormValue.length > 0 && (
                    <Typography className="text-muted-foreground text-xs">
                      Each matter will be charged{" "}
                      <CurrencyWrapper value={splitAmount} />.
                    </Typography>
                  )}
                </div>
              )}

              <Typography variant="subHeading" className="mt-4">
                Balance remaining
                <CurrencyWrapper value={outstandingBalance} />
              </Typography>
            </fieldset>
            {expense && expense.trustChecks.length > 0 && (
              <div className="bg-light-primary/50 m-6 flex flex-col gap-y-2 rounded-md p-2">
                <Typography variant="heading">Trust checks</Typography>
                <div className="flex flex-col gap-y-2">
                  {expense.trustChecks.map((trustCheck) => (
                    <TrustCheckCard
                      key={trustCheck.id}
                      trustCheck={trustCheck}
                      matter={matter}
                      className="border-border border"
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </AppModal>
      </Form>

      {editing && expense && (
        <ActionConfirmationModal
          open={isDeleting}
          setOpen={setIsDeleting}
          title="Are you sure you want to delete this cost?"
          description="This action cannot be undone. This will remove the cost and all associated tasks and files."
          afterActionFn={handleDelete}
        />
      )}
    </>
  );
};
